/**
 * ::meta widgets:
 * - PrettyMetaWidget: L0 pretty widget for ::meta[module] / ::meta[config] block
 * - MetaButtonGroupWidget: Config button for ::meta blocks
 */

import { WidgetType } from "@codemirror/view";
import { t as translate } from "@magic-md/i18n";
import {
    createHoverDetail,
    createPrettySyntaxShell,
    createSyntaxBadge,
    createTextSpan,
    shieldGroupEvents,
} from "../shared/widgets";

function tr(key: string, params?: Record<string, unknown>): string {
    return translate("editor", key, params);
}

/* ── PrettyMetaWidget — L0 pretty widget replacing entire ::meta block ── */

export class PrettyMetaWidget extends WidgetType {
    constructor(
        readonly domain: string,
        readonly summary: string,
    ) {
        super();
    }
    eq(other: PrettyMetaWidget) {
        return other.domain === this.domain && other.summary === this.summary;
    }
    toDOM(): HTMLElement {
        const domainUpper = this.domain.charAt(0).toUpperCase();
        const icon = createSyntaxBadge("M", { sub: domainUpper });
        icon.classList.add("cm-magic-meta-pretty-icon");

        return createPrettySyntaxShell({
            wrapperClass: "cm-magic-meta-pretty",
            badge: icon,
            primary: [createTextSpan("cm-magic-meta-pretty-domain", this.domain)],
            detail: this.summary
                ? createHoverDetail("cm-magic-meta-pretty-detail", [
                    createTextSpan("cm-magic-hover-summary cm-magic-meta-pretty-summary", this.summary),
                ])
                : null,
        });
    }
    ignoreEvent(): boolean {
        return false;
    }
}

/* ── MetaButtonGroupWidget — Config (⚙) button for ::meta header ── */

export class MetaButtonGroupWidget extends WidgetType {
    constructor(
        readonly domain: string,
    ) {
        super();
    }

    eq(other: MetaButtonGroupWidget) {
        return other.domain === this.domain;
    }

    toDOM(): HTMLElement {
        const group = document.createElement("span");
        group.className = "cm-magic-btn-group cm-magic-meta-btn-group";

        // Prevent mouse events from reaching CodeMirror
        shieldGroupEvents(group);

        const domain = this.domain;

        // Config button — opens meta config popover
        const cfgBtn = document.createElement("span");
        cfgBtn.className = "cm-magic-btn cm-magic-btn-config";
        cfgBtn.textContent = "⚙";
        cfgBtn.title = tr("syntax.widget.meta.configure", { domain });
        cfgBtn.setAttribute("aria-label", cfgBtn.title);
        cfgBtn.setAttribute("aria-haspopup", "dialog");
        cfgBtn.setAttribute("role", "button");
        cfgBtn.tabIndex = 0;
        const selectMeta = () => {
            const rect = cfgBtn.getBoundingClientRect();
            const editorDom = cfgBtn.closest<HTMLElement>(".cm-editor");
            const detail: {
                domain: string;
                x: number;
                y: number;
                paneId?: string;
                tabId?: string;
            } = {
                domain,
                x: rect.left,
                y: rect.bottom,
            };
            if (editorDom?.dataset.magicPaneId) {
                detail.paneId = editorDom.dataset.magicPaneId;
            }
            if (editorDom?.dataset.magicTabId) {
                detail.tabId = editorDom.dataset.magicTabId;
            }
            window.dispatchEvent(
                new CustomEvent("magic-md:select-meta", {
                    detail,
                }),
            );
        };
        cfgBtn.addEventListener("mousedown", (e) => {
            e.preventDefault();
            e.stopPropagation();
        });
        cfgBtn.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            selectMeta();
        });
        cfgBtn.addEventListener("keydown", (e) => {
            if (e.key !== "Enter" && e.key !== " ") return;
            e.preventDefault();
            e.stopPropagation();
            selectMeta();
        });

        group.appendChild(cfgBtn);

        // Snapshot run button — only for ::meta[snapshot] domain
        if (domain === "snapshot") {
            const snapBtn = document.createElement("span");
            snapBtn.className = "cm-magic-btn cm-magic-btn-snapshot";
            snapBtn.textContent = "◉";
            snapBtn.title = tr("syntax.widget.meta.snapshotDocument");
            snapBtn.setAttribute("aria-label", snapBtn.title);
            snapBtn.addEventListener("mousedown", (e) => {
                e.preventDefault();
                e.stopPropagation();
                window.dispatchEvent(
                    new CustomEvent("magic-md:snapshot-document", {
                        detail: { trigger: "manual" },
                    }),
                );
            });
            group.appendChild(snapBtn);
        }

        return group;
    }

    ignoreEvent(): boolean {
        return true;
    }
}
